import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot } from "lucide-react";
import api, { fmtDateTime } from "../../lib/api";
import { NdrActions } from "../app/NdrCenter";

const STATUS_CLS = {
  NEW: "bg-red-100 text-red-700", CONTACT_PENDING: "bg-amber-50 text-amber-700", CUSTOMER_CONTACTED: "bg-blue-50 text-blue-700",
  REATTEMPT_REQUESTED: "bg-violet-100 text-violet-700", ADDRESS_UPDATED: "bg-cyan-50 text-cyan-700",
  RESCHEDULED: "bg-cyan-50 text-cyan-700", RTO_REQUESTED: "bg-red-100 text-red-700", RESOLVED: "bg-emerald-100 text-emerald-700",
};

export default function AdminNdr() {
  const [cases, setCases] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => api.get("/admin/ndr", { params: filter ? { status: filter } : {} }).then(({ data }) => setCases(data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-ndr-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">NDR Center</h1>
          <p className="text-sm text-slate-500">All delivery exceptions across customers, with full action history.</p>
        </div>
        <div className="flex gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-ndr-filter"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-shipany-blue focus:outline-none">
            <option value="">All statuses</option>
            {Object.keys(STATUS_CLS).map((s) => <option key={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <Link to="/admin/ndr-rules" data-testid="ndr-automation-link" className="flex items-center gap-2 rounded-xl bg-shipany-navy px-4 py-2.5 text-sm font-bold text-white hover:bg-shipany-blue">
            <Bot className="h-4 w-4" /> Automation Rules
          </Link>
        </div>
      </div>

      <div className="mt-6 space-y-4" data-testid="admin-ndr-list">
        {(cases || []).map((c) => (
          <div key={c.case_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-ndr-case-${c.awb}`}>
            <div className="flex flex-wrap items-center gap-3">
              <p className="mono font-bold text-shipany-blue">{c.awb}</p>
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${STATUS_CLS[c.status]}`}>{c.status.replace(/_/g, " ")}</span>
              {c.automation_log?.length > 0 && <span className="flex items-center gap-1 rounded-full bg-shipany-cyan/15 px-2 py-0.5 text-[10px] font-bold text-shipany-blue"><Bot className="h-3 w-3" />{c.automation_log.length} auto actions</span>}
            </div>
            <p className="mt-1.5 text-sm text-slate-600">{c.customer_name} · <span className="mono">{c.customer_phone}</span> · {c.destination_city} · {c.courier_name}</p>
            <p className="mt-0.5 text-xs text-slate-400">Attempt #{c.attempt} · {c.reason} · {fmtDateTime(c.created_at)}</p>
            <div className="mt-4"><NdrActions c={c} onDone={load} /></div>
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-400 hover:text-shipany-blue">History ({c.actions?.length || 0})</summary>
              <div className="mt-2 space-y-1.5">
                {(c.actions || []).map((a, i) => (
                  <p key={i} className="mono rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">{a.at?.slice(0, 16).replace("T", " ")} · {a.action} · {a.by}{a.note ? ` — ${a.note}` : ""}{a.new_address ? ` → ${a.new_address}` : ""}</p>
                ))}
              </div>
            </details>
          </div>
        ))}
        {cases && cases.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No NDR cases.</div>}
      </div>
    </div>
  );
}
